import React, { useMemo, useEffect, useState, useCallback } from "react";
import { DatePicker, Modal, Select, Tooltip, Button, Input, notification, Checkbox } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBars } from "@fortawesome/pro-regular-svg-icons";
import RoutePatrolSvg from "./RoutePatrolSvg";
import ELRMileAndChain from "../util/ELRMileAndChain";
import {
    getPatrolPlans,
    receivePatrolPlan,
    getPatrolDeviceConfig,
    patrolImagesLoaded,
    createPatrol,
    clearPatrolState,
    getPatrolRuns,
    getPatrolDirectionsV2,
    togglePatrolDetailModal,
    toggleSettingsOpen,
    getStaticPatrolImages,
    clearPatrolImages,
} from "../../redux/actions/index";
import { useHistory, withRouter } from "react-router-dom";
import _ from "lodash";
import { faCog, faProjectDiagram } from "@fortawesome/pro-solid-svg-icons";
import moment from "moment";
import UnitSelectionModal from "components/UnitSelectionModal";
import { calculatePatrolDeviceConfig } from "components/util/Geometry";
import { ReactComponent as DetailRailIcon } from "../display/image/RailButtons/DetailRailIcon.svg";
import OBCSpinner from "components/util/OBC";
import { useLocation } from "react-router-dom";

const viewedProgressSelector = (state) => state.schemaInterface.covered;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const schemaLocationSelector = (state) => state.schemaInterface.location;
const schemaPlanSelector = (state) => state.schemaInterface.plan;
const patrolPlansSelector = (state) => state.schemaInterface.patrolPlans;
const superAdminSelector = (state) => state.userDetails.userConfig.super_admin;
const patrolRunsSelector = (state) => state.schemaInterface.patrolRuns.list;
const patrolRunsHasMoreSelector = (state) => state.schemaInterface.patrolRuns.has_more;
const detailModalOpenSelector = (state) => state.schemaInterface.detailModalOpen;
const railImageConfigsSelector = (state) => state.schemaInterface.config;
const railImagesSelector = (state) => state.schemaInterface.images;

const CreatePatrolWindow = ({ open, closeWindow, planID, startTimestamp, endTimestamp, device }) => {
    const [name, setName] = useState("");
    const [dueTs, setDueTs] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sendEmail, setSendEmail] = useState(false);

    const dispatch = useDispatch();

    const cancel = () => {
        setName("");
        setDueTs(0);
        setLoading(false);
        closeWindow();
    };

    const createRoutePatrol = () => {
        setLoading(true);
        const due = dueTs ? dueTs.unix() : 0;
        dispatch(
            createPatrol(planID, name, startTimestamp, endTimestamp, device, due, sendEmail, (success) => {
                if (success) {
                    cancel();
                    notification.success({ message: "Successfully created patrol" });
                } else {
                    notification.error({ message: "Error creating patrol" });
                }
            }),
        );
    };

    return (
        <Modal
            onCancel={cancel}
            visible={open}
            footer={[
                <Checkbox
                    value={sendEmail}
                    onChange={(e) => {
                        setSendEmail(e.target.checked);
                    }}>
                    Send email
                </Checkbox>,
                <Button
                    onClick={cancel}
                    loading={loading}>
                    Cancel
                </Button>,
                <Button
                    onClick={createRoutePatrol}
                    type="primary"
                    disabled={!name}
                    loading={loading}>
                    Create
                </Button>,
            ]}>
            <div className="CreatePatrolContainer">
                <div className="PatrolInputContainer">
                    <span>Patrol Name</span>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div className="PatrolInputContainer">
                    <span>Due Date</span>
                    <DatePicker
                        value={dueTs}
                        onChange={setDueTs}
                    />
                </div>
            </div>
        </Modal>
    );
};

const PatrolCreatorHeader = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const location = useLocation();

    const runIdFromUrl = useMemo(() => {
        const value = new URLSearchParams(location.search).get("run");
        if (!value) {
            return null;
        }
        const parsed = parseInt(value);
        return parsed ? parsed : null;
    }, [location.search]);

    const progressMap = useSelector(viewedProgressSelector);
    const elrUnits = useSelector(elrUnitsSelector);
    const currentLocation = useSelector(schemaLocationSelector);
    const schemaPlan = useSelector(schemaPlanSelector);
    const patrolPlans = useSelector(patrolPlansSelector);
    const superAdmin = useSelector(superAdminSelector);
    const patrolRuns = useSelector(patrolRunsSelector);
    const patrolRunsHasMore = useSelector(patrolRunsHasMoreSelector);
    const detailModalOpen = useSelector(detailModalOpenSelector);
    const railImageConfigs = useSelector(railImageConfigsSelector);
    const railImages = useSelector(railImagesSelector);

    const [createWindowOpen, setCreateWindowOpen] = useState(false);
    const [diagramOpen, setDiagramOpen] = useState(false);

    const [dateRange, setDateRange] = useState([null, null]);
    const [deviceGroup, setDeviceGroup] = useState(undefined);
    const [unitSelectionModalVisible, setUnitSelectionModalVisible] = useState(false);
    const [selectedRunID, setSelectedRunID] = useState(undefined);
    const [loadedPage, setLoadedPage] = useState(0);
    const [isLoadingRuns, setIsLoadingRuns] = useState(false);

    const planID = parseInt(props.match.params.planID);

    const onRunSelect = (runID) => {
        dispatch(clearPatrolImages());
        setSelectedRunID(runID);
        const run = _.find(patrolRuns, { id: runID });
        setDateRange([moment.unix(run.start_ts), moment.unix(run.end_ts)]);
        setDeviceGroup(run.device_group);

        dispatch(getStaticPatrolImages(`https://patrol-run.aivr.video/${run.image_file_uuid}.json`));
        dispatch(getPatrolDeviceConfig(run.device_group));
        dispatch(getPatrolDirectionsV2(run.device_group, run.start_ts - 60 * 60 * 2, run.end_ts));
    };

    const initialised = useMemo(() => {
        return patrolPlans.length;
    }, [patrolPlans.length]);

    useEffect(() => {
        if (!initialised) {
            dispatch(getPatrolPlans());
        }
    }, [initialised, dispatch, planID]);

    useEffect(() => {
        setIsLoadingRuns(true);
        dispatch(
            getPatrolRuns(planID, 0, () => {
                setLoadedPage(0);
                setIsLoadingRuns(false);
            }),
        );
    }, [dispatch, planID]);

    useEffect(() => {
        // once runs have loaded, pre-select one if requested in the URL
        if (!runIdFromUrl) {
            return;
        }
        if (selectedRunID) {
            return;
        }
        if (!patrolRuns?.length) {
            return;
        }
        const exists = _.find(patrolRuns, { id: runIdFromUrl });
        if (exists) {
            onRunSelect(runIdFromUrl);
        }
    }, [runIdFromUrl, patrolRuns, selectedRunID]);

    useEffect(() => {
        const patrolPlan = _.cloneDeep(_.find(patrolPlans, { id: planID }));
        if (patrolPlan) {
            patrolPlan.svg = JSON.stringify(patrolPlan.svg);
            dispatch(receivePatrolPlan(patrolPlan));
            dispatch(patrolImagesLoaded(true));
        }
    }, [patrolPlans, dispatch, planID]);

    const distanceUnits = useMemo(() => {
        if (elrUnits === "elr_mile_chain") {
            return "ELR Mile & Chain";
        } else if (elrUnits === "elr_mile_yards") {
            return "ELR Mile & Yards";
        } else if (elrUnits === "elr_meterage") {
            return "ELR & Meterage";
        }
    }, [elrUnits]);

    const locationDisplay = useMemo(() => {
        if (!currentLocation.elr) {
            return null;
        }
        const fieldObj = {
            ELR: currentLocation.elr,
            METERAGE: currentLocation.chain * 20.1168,
            TRACK: currentLocation.trackID,
        };
        const location = ELRMileAndChain.from_fields("ELR Mile & Chain", "elr_meterage", fieldObj, true);
        return location.to_string(elrUnits, []);
    }, [currentLocation, elrUnits]);

    const onBackClick = () => {
        dispatch(clearPatrolState());
        history.push("/patrols");
    };

    const getRunName = (run) => {
        const startDate = moment.unix(run.start_ts).format("DD/MM/YYYY");
        const endDate = moment.unix(run.end_ts).format("DD/MM/YYYY");

        let device = "";
        if (run.device_group === 11) {
            device = "VIU 3 (Pre-upgrade)";
        } else if (run.device_group === 10) {
            device = "VIU 2 (Pre-upgrade)";
        } else if (run.device_group === 1158125) {
            device = "VIU 1";
        } else if (run.device_group === 1171445) {
            device = "VIU 2";
        }
        return `${startDate} - ${endDate} (${device} - ${run.coverage}% coverage)`;
    };

    const sortedRuns = useMemo(() => {
        return _.orderBy(patrolRuns, "start_ts", "desc");
    }, [patrolRuns]);

    const toggleDetailRails = () => {
        dispatch(togglePatrolDetailModal(!detailModalOpen));
    };

    const railImageConfig = useMemo(() => {
        return calculatePatrolDeviceConfig(railImageConfigs, railImages[0] ? railImages[0].timestamp / 1000 : 0);
    }, [railImageConfigs, railImages]);

    const hasDetailImages = useMemo(() => {
        if (_.isEmpty(railImageConfig)) {
            return false;
        }

        return !!railImageConfig.inspection_images.filter((config) => config.showInModal).length;
    }, [railImageConfig]);

    const loadMoreRuns = useCallback(() => {
        if (patrolRunsHasMore && !isLoadingRuns) {
            const nextPage = loadedPage + 1;
            setIsLoadingRuns(true);
            dispatch(
                getPatrolRuns(planID, nextPage, () => {
                    setLoadedPage(nextPage);
                    setIsLoadingRuns(false);
                }),
            );
        }
    }, [dispatch, planID, loadedPage, patrolRunsHasMore, isLoadingRuns]);

    const handlePopupScroll = useCallback(
        (e) => {
            const target = e.target;

            const isBottom = Math.ceil(target.scrollTop + target.offsetHeight) >= target.scrollHeight;

            if (isBottom && patrolRunsHasMore) {
                loadMoreRuns();
            }
        },
        [loadMoreRuns, patrolRunsHasMore],
    );

    const loadingRuns = useMemo(() => {
        return isLoadingRuns && patrolRuns.length === 0;
    }, [isLoadingRuns, patrolRuns.length]);

    return (
        <div className="SchemaContentWrapper">
            {unitSelectionModalVisible && <UnitSelectionModal onClose={() => setUnitSelectionModalVisible(false)} />}

            <CreatePatrolWindow
                planID={planID}
                startTimestamp={dateRange[0] ? dateRange[0].startOf("day").unix() : null}
                endTimestamp={dateRange[1] ? dateRange[1].endOf("day").unix() : null}
                device={deviceGroup}
                closeWindow={() => setCreateWindowOpen(false)}
                open={createWindowOpen}
            />

            <FontAwesomeIcon
                icon={faArrowLeft}
                className="BackIcon"
                onClick={onBackClick}
            />
            <div className="TopContainer">
                <div className="SchemaLocationContainer">
                    <p className="Units">{distanceUnits}</p>
                    <FontAwesomeIcon
                        style={{ marginRight: 13, marginLeft: -5 }}
                        className="RouteInfo-ELRSettings__Icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            setUnitSelectionModalVisible(true);
                        }}
                        icon={faCog}
                    />
                    <span className="Value">{!!locationDisplay ? locationDisplay : "Unknown"}</span>
                </div>
            </div>

            <div className="SchemaInfo">
                <div className="SchemaInfoInner">
                    <div
                        className="SchemaTitleContainer"
                        style={{ gap: 10 }}>
                        <h1>{schemaPlan.name ? schemaPlan.name : "Unnamed Patrol Diagram"}</h1>
                    </div>

                    <div className="label">Choose from recent shifts</div>

                    <Select
                        onChange={onRunSelect}
                        style={{ width: 350 }}
                        value={selectedRunID}
                        placeholder={loadingRuns ? "Loading shifts..." : "Choose a recent shift"}
                        onPopupScroll={handlePopupScroll}
                        loading={loadingRuns}
                        dropdownMatchSelectWidth={false}>
                        {sortedRuns.map((run) => {
                            return (
                                <Select.Option
                                    key={run.id}
                                    value={run.id}>
                                    {getRunName(run)}
                                </Select.Option>
                            );
                        })}
                        {patrolRunsHasMore && isLoadingRuns ? (
                            <Select.Option
                                disabled
                                key="more_runs"
                                value="more_runs">
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "4px" }}>
                                    <OBCSpinner size={16} /> Loading more shifts...
                                </div>
                            </Select.Option>
                        ) : (
                            <Select.Option
                                disabled
                                key="no_more_runs"
                                value="no_more_runs">
                                <div style={{ textAlign: "center" }}>{sortedRuns.length === 0 ? "No shifts found" : "No more shifts"}</div>
                            </Select.Option>
                        )}
                    </Select>
                </div>
            </div>

            <RoutePatrolSvg
                planData={schemaPlan}
                progressMap={progressMap}
            />

            <Modal
                title="Track Patrolling Diagram"
                visible={diagramOpen}
                width={800}
                cancelText={"Close"}
                okText={"Report a problem"}
                onCancel={() => setDiagramOpen(false)}
                footer={[<Button onClick={() => setDiagramOpen(false)}>Close</Button>]}>
                <img
                    style={{ width: "100%" }}
                    alt="track Diagram"
                    src={schemaPlan.diagram_link}
                    crossOrigin={"anonymous"}
                />
            </Modal>

            <div className="SchemaToolbar">
                {superAdmin && (
                    <Button
                        disabled={!dateRange[0] || !dateRange[1] || !deviceGroup}
                        // disabled={false}
                        onClick={() => setCreateWindowOpen(true)}
                        type="primary">
                        Create Patrol
                    </Button>
                )}

                {hasDetailImages && (
                    <Tooltip title="Show Detailed Rail Images">
                        <div
                            onClick={toggleDetailRails}
                            className={"SchemaToolbarItem" + (detailModalOpen ? " Active" : "")}>
                            <DetailRailIcon />
                        </div>
                    </Tooltip>
                )}

                {schemaPlan.diagram_link && (
                    <Tooltip title="View Track Patrolling Diagram">
                        <div
                            className={"SchemaToolbarItem"}
                            onClick={() => setDiagramOpen(true)}>
                            <FontAwesomeIcon
                                icon={faProjectDiagram}
                                className="Icon"
                            />
                        </div>
                    </Tooltip>
                )}

                <Tooltip title="Navigation">
                    <div
                        className={"SchemaToolbarItem" + (props.assetNavigationActive ? " Active" : "")}
                        onClick={() => props.setAssetNavigationOpen(!props.assetNavigationActive)}>
                        <FontAwesomeIcon
                            icon={faBars}
                            className="Icon"
                        />
                    </div>
                </Tooltip>

                <Tooltip title="Settings">
                    <div
                        className="SchemaToolbarItem"
                        onClick={() => dispatch(toggleSettingsOpen())}>
                        <FontAwesomeIcon
                            icon={faCog}
                            className="Icon"
                        />
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};

export default withRouter(PatrolCreatorHeader);
